import { isPresent } from "@suse-o11y/is-present";
import { prop } from "@suse-o11y/prop";
import type { Template } from "@suse-o11y/template";

import { Metric } from "~/models/metric";

import type { TimeRange } from "../../../app/state/view/timeline/TimeRange";
import { type InstantPromQLResult, queryInstantPromQL } from "../../../queries/metrics/batch";
import { queryMetricsLabelValues, queryMetricsLabels } from "../../../queries/metrics/labels";
import type { UseFullQueryOptions } from "../../../queries/react-query";

import type { DynamicListVariable } from "./_derived-types/dynamic-list-variable";

type MetricMatchers = ReadonlyArray<undefined | string>;

function isUsableMatcher(matcher: undefined | string): matcher is string {
	return isPresent(matcher) && matcher.length > 0;
}

export function queryMetricLabelNameOptions({
	matchers,
	timeRange,
}: {
	matchers: MetricMatchers;
	timeRange: TimeRange;
}) {
	return {
		enabled: matchers.every(isPresent),
		...queryMetricsLabels({
			matches: matchers.filter(isUsableMatcher),
			timeRange,
		}),
	} as const satisfies UseFullQueryOptions<ReadonlyArray<string>, Error, ReadonlyArray<string>>;
}

export function queryMetricLabelValueOptions({
	label,
	matchers,
	timeRange,
}: {
	label: string;
	matchers: MetricMatchers;
	timeRange: TimeRange;
}) {
	return {
		enabled: label.length > 0 && matchers.every(isPresent),
		...queryMetricsLabelValues({
			label,
			matches: matchers.filter(isUsableMatcher),
			timeRange,
		}),
	} as const satisfies UseFullQueryOptions<ReadonlyArray<string>, Error, ReadonlyArray<string>>;
}

export function queryMetricPromQLOptions({
	expression,
	label,
	timeRange,
}: {
	expression: undefined | string;
	label: string;
	timeRange: TimeRange;
}) {
	const query = expression ?? "";

	return {
		select: (result) =>
			result.transform((vectors) => Metric.extractLabelValuesByName(label, vectors.map(prop("metric")))).getOrElse([]),
		enabled: expression != null && query.length > 0,
		...queryInstantPromQL({
			step: undefined,
			query,
			timestamp: timeRange.end,
		}),
	} as const satisfies UseFullQueryOptions<InstantPromQLResult, Error, ReadonlyArray<string>>;
}

export function queryDynamicVariableOptions(
	variable: DynamicListVariable,
	timeRange: TimeRange,
	interpolate: (template: Template<["${", "}"], null>) => undefined | string,
): UseFullQueryOptions<unknown, Error, [variable: DynamicListVariable, values: ReadonlyArray<string>]> {
	const { plugin } = variable.spec.spec;

	if (plugin.kind === "MetricLabelNames") {
		return {
			select: (values: ReadonlyArray<string>) => [variable, values],

			...queryMetricLabelNameOptions({
				matchers: plugin.spec.matchers.map(interpolate),
				timeRange,
			}),
		};
	}

	if (plugin.kind === "MetricLabelValues") {
		return {
			select: (values: ReadonlyArray<string>) => [variable, values],

			...queryMetricLabelValueOptions({
				label: plugin.spec.labelName,
				matchers: plugin.spec.matchers.map(interpolate),
				timeRange,
			}),
		};
	}

	const { select, ...query } = queryMetricPromQLOptions({
		expression: interpolate(plugin.spec.expr),
		label: plugin.spec.labelName,
		timeRange,
	});

	return {
		select: (result: InstantPromQLResult) => [variable, select(result)],

		...query,
	};
}

export function multipleValuesToString(values: ReadonlyArray<string>): string {
	return `(${values.join("|")})`;
}
